import React, {
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    View
} from "react-native";
import type {
    LogsResponse,
    LogEntry
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateTimePicker,
    PageContainer,
    TextInput,
    SelectBox,
    StateCard,
    Button,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    ListIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const LogsPage = () => {
    const {
        user
    } = React.useContext(AuthContext);

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        width
    } = useWindowDimensions();

    const isMobile = width < 768;

    const [
        logs,
        setLogs
    ] = useState<LogEntry[]>([]);

    const [
        organizations,
        setOrganizations
    ] = useState<Array<{ id: string; name: string }>>([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        total,
        setTotal
    ] = useState(0);

    const [
        resetKey,
        setResetKey
    ] = useState<number>(0);

    const [
        filters,
        setFilters
    ] = useState({
        organization_id: "",
        startDate: "",
        endDate: "",
        search: "",
        limit: 20,
        page: 1
    });

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [filters]);

    const fetchOrganizations = async () => {
        try {
            const token = await AsyncStorage.getItem("authToken");

            const res = await fetch(`${process.env.API_URL}/organizations?limit=1000`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data = await res.json();

            if (data.status === "success") {
                const orgsArray = Array.isArray(data.data)
                    ? data.data
                    : (Array.isArray(data.data?.data) ? data.data.data : []);

                setOrganizations(orgsArray);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);

        try {
            const query = new URLSearchParams({
                organization_id: filters.organization_id,
                limit: filters.limit.toString(),
                page: filters.page.toString(),
                startDate: filters.startDate,
                endDate: filters.endDate,
                search: filters.search
            }).toString();

            const token = await AsyncStorage.getItem("authToken");

            const res = await fetch(`${process.env.API_URL}/logs?${query}`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            const data: LogsResponse = await res.json();

            if (data.status === "success") {
                setLogs(data.data);
                setTotal(data.total);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const totalPages = Math.ceil(total / filters.limit);

    const mappedOrgs = React.useMemo(() => {
        return organizations.map(org => ({
            __title: org.name,
            __key: org.id
        }));
    }, [organizations]);

    if (user?.role !== "root") {
        return <PageContainer>
            <View
                style={[
                    stylesheet.container,
                    {
                        padding: spaces.spacingXl,
                        justifyContent: "center",
                        alignItems: "center"
                    }
                ]}
            >
                <Text>Yetkisiz erişim.</Text>
            </View>
        </PageContainer>;
    }

    return <PageContainer
        scrollViewStyle={isMobile ? [
            stylesheet.container,
            {
                padding: spaces.spacingMd
            }
        ] : undefined}
        isScrollable={true}
    >
        <View
            style={isMobile ? undefined : [
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("logs-page-title")}
                </Text>
                <View
                    style={[
                        {
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchLogs();
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        onPress={() => {
                            setFilters({
                                organization_id: "",
                                startDate: "",
                                endDate: "",
                                search: "",
                                limit: 20,
                                page: 1
                            });
                            setResetKey(prev => prev + 1);
                        }}
                        title={localize("finance-clear")}
                        type="neutral"
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.filterContainer,
                    {
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={{
                        minWidth: isMobile ? "100%" : 200,
                        flex: 1
                    }}
                >
                    <TextInput
                        onChangeText={(text) => handleFilterChange("search", text)}
                        placeholder={localize("logs-search-placeholder")}
                        title={localize("logs-search-title")}
                        spreadBehaviour="stretch"
                        value={filters.search}
                    />
                </View>
                <View
                    style={{
                        minWidth: isMobile ? "100%" : 200,
                        flex: 1
                    }}
                >
                    <SelectBox
                        onChange={(selected) => handleFilterChange("organization_id", selected[0]?.__key || "")}
                        initialSelectedItems={mappedOrgs.filter(o => o.__key === filters.organization_id)}
                        key={`org-filter-${mappedOrgs.length}-${resetKey}`}
                        placeholder={localize("org-selector-placeholder")}
                        title={localize("org-selector-title")}
                        titleExtractor={(i) => i.__title}
                        keyExtractor={(i) => i.__key}
                        spreadBehaviour="stretch"
                        data={mappedOrgs}
                    />
                </View>
                <View
                    style={[
                        stylesheet.filterItem,
                        {
                            minWidth: isMobile ? "100%" : 200,
                            flex: 1
                        }
                    ]}
                >
                    <DateTimePicker
                        onChange={({
                            dateRange
                        }) => {
                            setFilters(prev => ({
                                ...prev,
                                startDate: dateRange?.start ? new Date(dateRange.start).toISOString() : "",
                                endDate: dateRange?.end ? new Date(dateRange.end).toISOString() : "",
                                page: 1
                            }));
                        }}
                        placeholder={localize("logs-date-filter-placeholder")}
                        title={localize("logs-date-filter-title")}
                        key={`date-filter-${resetKey}`}
                        spreadBehaviour="stretch"
                        variant="range"
                    />
                </View>
            </View>
            {
                loading && logs.length === 0 ?
                    <View
                        style={stylesheet.loaderContainer}
                    >
                        <ActivityIndicator
                            color={colors.content.text.high}
                            size="large"
                        />
                    </View>
                :
                    logs.length === 0 ? (
                        <StateCard
                            icon={({
                                color,
                                size
                            }: { color: string; size: number }) => <ListIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />}
                            subTitle={localize("logs-empty-subtitle")}
                            title={localize("logs-empty-title")}
                        />) : (
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                <View
                                    style={[
                                        stylesheet.tableHeader,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            padding: spaces.spacingMd,
                                            borderRadius: radiuses.md
                                        }
                                    ]}
                                >
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="labelMediumSize"
                                    >
                                        {localize("logs-table-date")}
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="labelMediumSize"
                                    >
                                        {localize("logs-table-user")}
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="labelMediumSize"
                                    >
                                        {localize("org-selector-title")}
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="labelMediumSize"
                                    >
                                        {localize("logs-table-action")}
                                    </Text>
                                    {!isMobile && <Text
                                        style={stylesheet.tableCellLarge}
                                        variant="labelMediumSize"
                                    >
                                        {localize("logs-table-details")}
                                    </Text>}
                                </View>
                                {logs.map(log => <View
                                    style={[
                                        stylesheet.tableRow,
                                        {
                                            backgroundColor: colors.content.container.default,
                                            borderColor: colors.content.border.default,
                                            padding: spaces.spacingMd
                                        }
                                    ]}
                                    key={log.id}
                                >
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="bodySmallSize"
                                    >
                                        {new Date(log.createdAt).toLocaleString("tr-TR")}
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="bodySmallSize"
                                    >
                                        {log.username} ({log.role})
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="bodySmallSize"
                                    >
                                        {log.organization_id ? (organizations.find(o => o.id === log.organization_id)?.name || "Bilinmeyen Organizasyon") : "Sistem / Root"}
                                    </Text>
                                    <Text
                                        style={stylesheet.tableCellSmall}
                                        variant="bodySmallSize"
                                    >
                                        {log.action_type}
                                    </Text>
                                    {!isMobile && <Text
                                        style={stylesheet.tableCellLarge}
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {log.details}
                                    </Text>}
                                </View>)}
                                {totalPages > 1 && (
                                    <View
                                        style={[
                                            stylesheet.paginationContainer,
                                            {
                                                marginTop: spaces.spacingLg
                                            }
                                        ]}
                                    >
                                        <Text
                                            variant="bodySmallSize"
                                            color="mid"
                                        >
                                            Toplam {total} kayıttan {(filters.page - 1) * filters.limit + 1} - {Math.min(filters.page * filters.limit, total)} arası gösteriliyor.
                                        </Text>
                                        <View
                                            style={[
                                                stylesheet.paginationButtons,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Button
                                                icon={({
                                                    color,
                                                    size
                                                }: { color: string; size: number }) => <ChevronLeftIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />}
                                                onPress={() => setFilters(p => ({
                                                    ...p,
                                                    page: p.page - 1
                                                }))}
                                                isDisabled={filters.page === 1}
                                                type="neutral"
                                            />
                                            <Button
                                                icon={({
                                                    color,
                                                    size
                                                }: { color: string; size: number }) => <ChevronRightIcon
                                                    color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                    size={size}
                                                />}
                                                isDisabled={filters.page >= totalPages}
                                                onPress={() => setFilters(p => ({
                                                    ...p,
                                                    page: p.page + 1
                                                }))}
                                                type="neutral"
                                            />
                                        </View>
                                    </View>)}
                            </View>)
            }
        </View>
    </PageContainer>;
};
export default LogsPage;
